import { ChevronLeft, LogOut, Menu } from 'lucide-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { useSidebarStore } from '../_useSidebarStore';
import { useTopBarHandle } from '../_useTopBarHandle';

import CustomDropdown from '@/shared/components/inputs/CustomDropdown';
import { useAuth } from '@/shared/hooks/useAuth';
import { useAuthStore } from '@/shared/auth/authStore';
import { PATH, USER_ROLE, USER_ROLE_KR } from '@/shared/constants';

import ErpLogo from '@/shared/assets/svg/logo/erp-logo.svg?react';
import IconBell from '@/shared/assets/svg/common/icon-bell.svg?react';
import IconPersonOutline from '@/shared/assets/svg/common/icon-person-outline.svg?react';

/**
 * 앱 상단바 — 햄버거로 드로어를 열고, 내 정보 버튼으로 유저 메뉴(로그아웃)를 연다.
 * ERP GlobalNavbar(검색·유저메뉴·브랜딩)는 앱에 불필요해 최소 버전으로 대체.
 *
 * 현재 위치(title)·뒤로가기 여부(showBack)는 각 라우트의 `handle`(router/index.tsx)을
 * useTopBarHandle()로 읽는다. 페이지 컴포넌트가 mount 후 useEffect로 title을 set하는 방식과 달리,
 * handle은 라우터가 URL을 매칭하는 시점에 이미 정해진 정적 값이라 같은 렌더 틱에 바로 반영된다
 * → 페이지 전환 중 title 깜빡임·추가 리렌더가 없다.
 */
export default function AppTopBar() {
  const setMobileDrawerOpen = useSidebarStore((s) => s.setMobileDrawerOpen);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const userInfo = useAuthStore((s) => s.userInfo);

  const { title, showBack, hasRow } = useTopBarHandle();

  const roleKr = userInfo.userRole ? USER_ROLE_KR[userInfo.userRole as keyof typeof USER_ROLE_KR] : '';

  // My Page(설정 · 계정)는 학생 전용 라우트라 학생일 때만 노출 — 학부모는 아직 대응하는 화면이 없다
  const isStudent = userInfo.userRole === USER_ROLE.STUDENT;

  // ERP GlobalNavbar의 유저 드롭다운을 앱 규모로 축소 — 헤더(이름·역할·아이디, 선택 불가) + My Page + 로그아웃
  const userMenuOptions = [
    {
      value: '__none__',
      label: (
        <div className='flex flex-col gap-0.5'>
          <span className='text-blue-gray-900 font-semibold'>
            {userInfo.userName || '사용자'}
            {roleKr && <span className='text-blue-gray-500 ml-1 font-normal'>({roleKr})</span>}
          </span>
          {userInfo.loginId && <span className='text-blue-gray-500 text-[13px]'>{userInfo.loginId}</span>}
        </div>
      ),
    },
    ...(isStudent
      ? [
          {
            value: 'mypage',
            label: (
              <div className='flex items-center gap-2 text-gray-900'>
                <i className='tw-icon-wrap size-4'>
                  <IconPersonOutline className='size-4' />
                </i>
                My Page
              </div>
            ),
          },
        ]
      : []),
    {
      value: 'logout',
      label: (
        <div className='flex items-center gap-2 text-gray-900'>
          <i className='tw-icon-wrap size-4'>
            <LogOut className='size-4' />
          </i>
          로그아웃
        </div>
      ),
    },
  ];

  return (
    <S.Bar>
      {/* 아이콘 행 — 항상 h-14로 고정. 헤더 전체 높이(--nav-h)가 이 두 행의 합으로 고정돼 있어서
          여기 높이를 바꾸면 아래 title 행과 함께 --nav-h(MainLayout)도 같이 맞춰야 한다 */}
      <div className='flex h-14 w-full items-center justify-between'>
        <div className='flex min-w-0 items-center gap-3'>
          <button
            type='button'
            aria-label='메뉴 열기'
            className='tw-flex-center custom_press_fx -ml-2 size-9 shrink-0 rounded-md active:bg-black/10'
            onClick={() => setMobileDrawerOpen(true)}>
            <Menu className='size-5 text-gray-900' />
          </button>

          <i className='tw-icon-wrap tw-flex-center shrink-0'>
            <ErpLogo />
          </i>

          {userInfo.userName && <span className='text-blue-gray-600 rounded-full bg-gray-100 px-2 py-0.5'>{userInfo.userName}</span>}
        </div>

        <div className='flex items-center gap-2'>
          <button
            type='button'
            aria-label='알림'
            className='custom_press_fx flex size-9 shrink-0 items-center justify-center rounded-lg border active:bg-gray-100'>
            <IconBell className='size-5 stroke-2 text-gray-900' />
          </button>

          <CustomDropdown
            options={userMenuOptions}
            onSelect={(option) => {
              if (option.value === 'mypage') navigate(PATH.STUDENT.SETTINGS.ACCOUNT);
              if (option.value === 'logout') logout();
            }}
            align='end'
            alignOffset={0}
            sideOffset={8}
            className='min-w-52 rounded-md!'
            dropdownContentClassName='py-1'
            optionClassName='px-3 py-2.5 my-0.5 rounded-lg font-medium text-blue-gray-800 [&.custom-dropdown-item-disabled]:opacity-100 hover:!bg-support-blue-shade hover:!text-support-blue'>
            <button
              type='button'
              aria-label='내 정보'
              className='custom_press_fx flex size-9 shrink-0 items-center justify-center rounded-lg border outline-none active:bg-gray-100 data-[state=open]:bg-gray-100'>
              <IconPersonOutline className='size-5 stroke-2 text-gray-900' />
            </button>
          </CustomDropdown>
        </div>
      </div>

      {hasRow && (
        <div className='flex h-12 w-full items-center gap-1'>
          {showBack && (
            <button
              type='button'
              aria-label='뒤로가기'
              className='tw-flex-center custom_press_fx -ml-2 size-9 shrink-0 rounded-md active:bg-black/10'
              onClick={() => navigate(-1)}>
              <ChevronLeft className='size-5 text-gray-900' />
            </button>
          )}
          {title && <span className='min-w-0 truncate font-semibold text-gray-900'>{title}</span>}
        </div>
      )}
    </S.Bar>
  );
}

const S = {
  Bar: styled.header`
    grid-row: 1;
    grid-column: 1 / -1;
    /* 드로어 백드롭(z-49)·Aside(z-50) 위에 떠서 어둡게 덮이지 않게 한다 */
    position: relative;
    z-index: 51;
    display: flex;
    flex-direction: column;
    /* 아이콘 행(h-14) + (title 행이 있을 때만 h-9) — MainLayout이 useTopBarHandle()의 같은 hasRow로
       --nav-h를 이 높이와 동일하게 계산한다. 여기서 행 렌더 조건을 바꾸면 MainLayout의 hasRow 계산도
       같이 바꿔야 한다 — 어긋나면 title 행이 잘리거나 드로어(top: --header-h)가 겹쳐 보인다. */
    /* 안전영역 패딩을 height 안에 포함시켜야 한다(border-box) — height를 nav-h로 고정한 채
       padding-top에 safe-area를 또 넣으면 노치 있는 기기에서 패딩이 박스 높이를 넘어 콘텐츠가 찌그러진다 */
    height: var(--header-h, var(--nav-h));
    padding: env(safe-area-inset-top) 1rem 0;
    border-bottom: 1px solid var(--color-table-line, #e5eaf8);
    background: var(--color-basic-white, #fff);
  `,
};
